/**
 * The status glyph on a Production Records day card: ONE slot that morphs
 * with the record. A progress ring with the percent inside while the day is
 * open (an empty ring at 0%, a dashed one while the materializer has not
 * generated the records yet), a light green disc with a thin ring and a
 * green check once the day is complete. Light rather than solid on purpose:
 * a finished day should read quieter than one that still needs work. The
 * card's accessible name carries the status in words, so the glyph itself
 * is decorative.
 */

import { cn } from '@ordo/ui/cn'
import { Check } from 'lucide-react'

import type { DayProgress } from '@/lib/production-records/day-progress'

const RADIUS = 16.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export type GlyphSize = 'md' | 'sm'

const SIZE_CLASS: Record<GlyphSize, string> = { md: 'h-10 w-10', sm: 'h-9 w-9' }

/** `stroke-dasharray` for the arc: the visible length for a percent, then the whole circumference. */
export function ringDasharray(percent: number): string {
  const clamped = Math.min(100, Math.max(0, percent))
  return `${((clamped / 100) * CIRCUMFERENCE).toFixed(2)} ${CIRCUMFERENCE.toFixed(2)}`
}

export function DayStatusGlyph({
  progress,
  size = 'md',
  className,
}: {
  progress: Pick<DayProgress, 'complete' | 'percent' | 'awaitingRecords'>
  size?: GlyphSize
  className?: string
}) {
  if (progress.complete) {
    return (
      <span
        data-testid="day-glyph"
        data-state="complete"
        aria-hidden
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full bg-emerald-500/10 text-emerald-700 ring-1 ring-inset ring-emerald-600/30',
          'dark:bg-emerald-500/15 dark:text-emerald-400 dark:ring-emerald-400/30',
          SIZE_CLASS[size],
          className,
        )}>
        <Check className="h-5 w-5" strokeWidth={2.5} />
      </span>
    )
  }
  const showArc = progress.percent > 0 && !progress.awaitingRecords
  return (
    <span
      data-testid="day-glyph"
      data-state={progress.awaitingRecords ? 'awaiting' : 'open'}
      aria-hidden
      className={cn('relative block shrink-0', SIZE_CLASS[size], className)}>
      <svg viewBox="0 0 40 40" className="block h-full w-full">
        <circle
          cx="20"
          cy="20"
          r={RADIUS}
          fill="none"
          strokeWidth="3.5"
          strokeDasharray={progress.awaitingRecords ? '4 4' : undefined}
          className={progress.awaitingRecords ? 'stroke-muted-foreground/40' : 'stroke-border'}
        />
        {showArc ? (
          <circle
            cx="20"
            cy="20"
            r={RADIUS}
            fill="none"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray={ringDasharray(progress.percent)}
            transform="rotate(-90 20 20)"
            className="stroke-orange-600"
          />
        ) : null}
      </svg>
      {progress.awaitingRecords ? null : (
        <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold tabular-nums text-foreground">
          {progress.percent}%
        </span>
      )}
    </span>
  )
}
